import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { API } from "../App";
import { Send, Sparkles, AlertTriangle, Stethoscope, ShieldAlert, Check, X } from "lucide-react";

export default function SymptomChecker() {
  const location = useLocation();
  const [pets, setPets] = useState([]);
  const [petId, setPetId] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: "Hi! I'm Dr. PetBond — your AI vet buddy. Tell me what's going on with your pet (symptoms, age, what they ate, anything unusual). I'll suggest next steps. Note: I don't replace an in-person vet exam." }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showVetCTA, setShowVetCTA] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(null); // null = unknown, true=show, false=dismissed
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/pets`).then(r => {
      setPets(r.data);
      const presetPet = location.state?.petId;
      setPetId(presetPet || r.data[0]?.pet_id || "");
    });
    axios.get(`${API}/me/ai-disclaimer`).then(r => setShowDisclaimer(!r.data.accepted)).catch(() => setShowDisclaimer(true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const acceptDisclaimer = async () => {
    await axios.post(`${API}/me/ai-disclaimer/accept`, { accepted_version: "v1" }).catch(() => {});
    setShowDisclaimer(false);
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const myMsg = { role: "user", content: input };
    setMessages(prev => [...prev, myMsg]);
    setInput("");
    setSending(true);
    try {
      const r = await axios.post(`${API}/symptom-chat/safe`, {
        message: myMsg.content,
        pet_id: petId || null,
        session_id: sessionId,
      });
      setSessionId(r.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: r.data.reply, emergency: r.data.emergency }]);
      if (r.data.emergency) {
        setShowVetCTA(true);
      } else {
        const reply = (r.data.reply || "").toLowerCase();
        if (/(urgent|emergency|immediately|right away|asap|as soon as possible|soon|see a vet|visit a vet|consult a vet|veterinary care|veterinary attention|book.*vet|find.*vet|take.*to.*vet|transport.*to.*vet)/i.test(reply)) {
          setShowVetCTA(true);
        }
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry — I'm having trouble reaching the AI service right now. **In the meantime:** if your pet is showing severe symptoms (bleeding, seizures, collapse, breathing trouble) — go to the nearest vet hospital immediately. For routine concerns, please try again in a few minutes." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="symptom-checker-page">
      {showDisclaimer === true && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="ai-disclaimer-modal">
          <div className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft">
            <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand grid place-items-center mb-3"><ShieldAlert className="w-6 h-6"/></div>
            <h3 className="font-heading text-xl font-extrabold">Before we chat — a quick note</h3>
            <p className="text-sm text-ink-soft mt-2">Dr. PetBond AI is a helpful guide, but <b className="text-ink">not a replacement for a licensed veterinarian</b>. Please:</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-green mt-0.5 shrink-0"/>Use it for general guidance & next-step suggestions</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-green mt-0.5 shrink-0"/>For emergencies — go to a vet immediately, don't wait</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-brand-green mt-0.5 shrink-0"/>Don't change medications without consulting a real vet</li>
              <li className="flex gap-2"><X className="w-4 h-4 text-brand mt-0.5 shrink-0"/>Never use it for life-threatening symptoms (bleeding, seizures, collapse)</li>
            </ul>
            <button onClick={acceptDisclaimer} data-testid="accept-disclaimer" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">I understand — let's chat</button>
            <Link to="/app/vets" className="block text-center text-xs text-ink-soft mt-3 hover:text-brand">Or skip AI — find a real vet instead →</Link>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-10 h-10 rounded-2xl bg-leaf grid place-items-center"><Sparkles className="w-5 h-5 text-ink"/></div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold leading-none">Dr. PetBond</h1>
          <p className="text-sm text-ink-soft">Your AI symptom buddy — friendly, fast, and always kind.</p>
        </div>
      </div>

      <div className="bg-sunshine/30 border border-sunshine rounded-2xl p-3 text-sm flex items-start gap-2 my-4">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
        <span><strong>Not a substitute for in-person veterinary care.</strong> For emergencies (bleeding, seizures, breathing trouble), visit a vet immediately.</span>
      </div>

      {pets.length > 0 && (
        <div className="mb-3">
          <label className="text-xs font-bold uppercase tracking-wider text-ink-soft">For which pet?</label>
          <select data-testid="chat-pet-select" value={petId} onChange={e=>setPetId(e.target.value)} className="mt-1 bg-surface border border-line rounded-full px-4 py-2 font-bold text-sm">
            <option value="">— No specific pet —</option>
            {pets.map(p => <option key={p.pet_id} value={p.pet_id}>{p.name} · {p.breed}</option>)}
          </select>
        </div>
      )}

      <div className="bg-surface border border-line rounded-3xl shadow-soft p-4 h-[55vh] overflow-y-auto space-y-3" data-testid="chat-window">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-3xl px-4 py-2.5 text-[15px] ${m.role === "user" ? "bg-brand text-white rounded-br-md" : "bg-surface-muted text-ink rounded-bl-md"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-surface-muted rounded-3xl px-4 py-2.5 text-sm text-ink-soft">Dr. PetBond is thinking…</div>
          </div>
        )}
        <div ref={endRef}/>
      </div>

      {showVetCTA && (
        <div className="mt-3 bg-brand/10 border border-brand rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="vet-cta-banner">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-brand"/>
            <div>
              <div className="font-bold text-sm">Dr. PetBond suggests a vet visit.</div>
              <div className="text-xs text-ink-soft">Book a verified vet near you in 2 taps.</div>
            </div>
          </div>
          <Link to="/app/vets" state={{ petId }} className="bg-brand text-white rounded-full px-5 py-2 text-sm font-bold hover:bg-brand-hover" data-testid="vet-cta-button">Find a vet →</Link>
        </div>
      )}

      <form onSubmit={send} className="mt-3 flex gap-2">
        <input
          data-testid="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Describe the symptom — e.g. 'Biscuit has been scratching ears'"
          className="flex-1 bg-surface border border-line rounded-full px-5 py-3 outline-none focus:border-brand font-body"
        />
        <button data-testid="chat-send" disabled={sending} className="bg-brand text-white rounded-full w-12 h-12 grid place-items-center disabled:opacity-60 hover:bg-brand-hover">
          <Send className="w-5 h-5"/>
        </button>
      </form>
    </div>
  );
}
